import React, { useContext, useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { SidebarContext } from './SidebarContext';
import ApiURL from '../Api/Api';
import { fetchJson } from '../utils/http';
import { formatKolkataDateTime } from '../utils/dateFormat';
import TransactionProofImage from './TransactionProofImage';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CalendarDaysIcon,
  FunnelIcon,
  ArrowPathIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ScaleIcon,
  DocumentArrowDownIcon,
  ChevronRightIcon,
  XMarkIcon,
  BuildingOffice2Icon,
  TableCellsIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';

/* â”€â”€ helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const fmt = (v) =>
  v != null
    ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(v))
    : 'â‚¹0';

const fmtDate = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (v) => {
  const formatted = formatKolkataDateTime(v);
  return formatted === '-' ? '-' : formatted;
};

/* â”€â”€ stat card â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const StatCard = ({ label, value, icon: Icon, bg, iconBg, textColor, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.3 }}
    className={`${bg} rounded-2xl p-3.5 sm:p-4 flex items-center gap-3`}
  >
    <div className={`${iconBg} w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm`}>
      <Icon className="w-4.5 h-4.5 text-white w-5 h-5" />
    </div>
    <div className="min-w-0">
      <p className={`text-sm sm:text-base font-bold ${textColor} leading-tight`}>{value}</p>
      <p className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-wider font-semibold">{label}</p>
    </div>
  </motion.div>
);

/* â”€â”€ type badge â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const TypeBadge = ({ type }) =>
  type === 'credit' ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full border border-emerald-200">
      <ArrowTrendingUpIcon className="w-2.5 h-2.5" /> Credit
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 text-[10px] font-bold rounded-full border border-red-200">
      <ArrowTrendingDownIcon className="w-2.5 h-2.5" /> Debit
    </span>
  );

/* â”€â”€ status badge â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const StatusBadge = ({ status }) => {
  const s = (status || 'received').toUpperCase();
  const map = {
    SUBMITTED: 'bg-blue-50 text-blue-700 border-blue-200',
    SAVED:     'bg-amber-50 text-amber-700 border-amber-200',
    SAVE:      'bg-amber-50 text-amber-700 border-amber-200',
    RECEIVED:  'bg-slate-50 text-slate-600 border-slate-200',
    SUBMIT:    'bg-blue-50 text-blue-700 border-blue-200',
    RETURN:    'bg-orange-50 text-orange-700 border-orange-200',
    APPROVED:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  return (
    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full border ${map[s] || map.RECEIVED}`}>{s}</span>
  );
};

/* â”€â”€ Mobile Card Component (optimized for touch) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const getTravelInfo = (txn) => {
  const from = txn.from_destination || txn.from || '';
  const to = txn.to_destination || txn.to || '';
  const mode = txn.travel_mode || '';
  return { from, to, mode };
};

const isTravelTransaction = (txn) =>
  String(txn.main_head || '').trim().toLowerCase() === 'travel';

const DetailRow = ({ label, value, wide = false }) => (
  <div className={wide ? 'sm:col-span-2' : ''}>
    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">{label}</p>
    <p className="min-h-[34px] rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-[12px] font-semibold text-slate-700 break-words">
      {value || '-'}
    </p>
  </div>
);

const TransactionDetailModal = ({ txn, onClose }) => {
  if (!txn) return null;
  const travel = getTravelInfo(txn);
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center px-3 py-6"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 18, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.98 }}
          transition={{ duration: 0.18 }}
          className="w-full max-w-3xl max-h-[88vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-slate-100"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-slate-100 px-5 py-4 flex items-center justify-between">
            <div>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Transaction Details</p>
              <h2 className="text-base font-bold text-slate-800 mt-0.5">#{txn.display_transaction_no || txn.transaction_no || '-'}</h2>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-xl border border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-50 flex items-center justify-center"
              aria-label="Close details"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>

          <div className="p-5 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <DetailRow label="Date" value={fmtDateTime(txn.transaction_date)} />
              <DetailRow label="Type" value={txn.amount_type} />
              <DetailRow label="Status" value={txn.form_status} />
              <DetailRow label="Credit" value={txn.amount_type === 'credit' ? fmt(txn.credit_amount) : '-'} />
              <DetailRow label="Debit" value={txn.amount_type === 'debit' ? fmt(txn.debit_amount) : '-'} />
              <DetailRow label="Balance" value={fmt(txn.net_balance)} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DetailRow label="Particulars / Given To" value={txn.particuler_to} wide />
              <DetailRow label="Site" value={txn.site} />
              <DetailRow label="Project" value={txn.project_name} />
              <DetailRow label="Main Head" value={txn.main_head} />
              <DetailRow label="Sub Head" value={txn.sub_head} />
            </div>

            {(isTravelTransaction(txn) || travel.from || travel.to || travel.mode) && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <DetailRow label="Source" value={travel.from} />
                <DetailRow label="Destination" value={travel.to} />
                <DetailRow label="Travel Mode" value={travel.mode} />
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DetailRow label="Remarks" value={txn.remarks} wide />
              <DetailRow label="Created By" value={txn.created_by} />
              <DetailRow label="Created At" value={fmtDateTime(txn.created_at)} />
              <DetailRow label="Updated By" value={txn.updated_by} />
              <DetailRow label="Updated At" value={txn.updated_at && txn.updated_at !== txn.created_at ? fmtDateTime(txn.updated_at) : ''} />
            </div>

            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Proof</p>
              <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                <TransactionProofImage path={txn.attachment_path} />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const TransactionCard = ({ txn, index, role, navigate, onView }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: Math.min(index * 0.03, 0.3) }}
    className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden active:scale-[0.99] transition-transform duration-75"
  >
    <div className={`h-0.5 w-full ${txn.amount_type === 'credit' ? 'bg-emerald-400' : 'bg-red-400'}`} />
    <div className="px-4 py-3.5">
      {/* Header row: date + type + amount */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-medium text-slate-400">{fmtDateTime(txn.transaction_date)}</span>
          <TypeBadge type={txn.amount_type} />
        </div>
        <p className={`text-base font-bold ${txn.amount_type === 'credit' ? 'text-emerald-600' : 'text-red-600'}`}>
          {txn.amount_type === 'credit' ? '+' : '-'}{fmt(txn.amount_type === 'credit' ? txn.credit_amount : txn.debit_amount)}
        </p>
      </div>

      {/* Party / Particulars */}
      <div className="mb-2">
        <p className="text-[15px] font-bold text-slate-800 leading-tight">{txn.particuler_to || '-'}</p>
        {txn.main_head && (
          <span className="inline-block mt-1 px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-semibold rounded-full border border-indigo-100">
            {txn.main_head}
          </span>
        )}
      </div>

      {/* Details row: site, balance, status */}
      <div className="flex flex-wrap items-center justify-between gap-2 mt-2 pt-2 border-t border-slate-50">
        <div className="flex flex-wrap items-center gap-2">
          {txn.site && (
            <span className="text-[11px] text-slate-400 flex items-center gap-1">
              <BuildingOffice2Icon className="w-3 h-3" /> {txn.site}
            </span>
          )}
          <StatusBadge status={txn.form_status} />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-slate-400">Bal {fmt(txn.net_balance)}</span>
          {role === 'admin' && txn.amount_type === 'credit' && (
            <button
              onClick={() => navigate(`/admin-return?highlight=${txn.transaction_no}`)}
              className="px-2.5 py-1 bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-bold rounded-lg active:bg-amber-100 transition-colors"
            >
              Return
            </button>
          )}
        </div>
      </div>

      {/* Metadata row: created, updated */}
      <div className="mt-2 pt-1 border-t border-slate-50 text-[9px] text-slate-400 flex flex-wrap items-center justify-between gap-1">
        <div className="flex items-center gap-2">
          <span>Created: {fmtDateTime(txn.created_at)}</span>
          {txn.created_by && <span>by {txn.created_by}</span>}
        </div>
        {txn.updated_at && txn.updated_at !== txn.created_at && (
          <div className="flex items-center gap-2">
            <span>Updated: {fmtDateTime(txn.updated_at)}</span>
            {txn.updated_by && <span>by {txn.updated_by}</span>}
          </div>
        )}
      </div>

      {/* Remarks if present */}
      {txn.remarks && (
        <p className="mt-2 text-[11px] text-slate-500 italic break-words">{txn.remarks}</p>
      )}
      {(isTravelTransaction(txn) || getTravelInfo(txn).from || getTravelInfo(txn).to || getTravelInfo(txn).mode) && (
        <div className="mt-2 rounded-xl bg-slate-50 border border-slate-100 px-3 py-2 text-[11px] text-slate-600">
          <p className="font-bold text-slate-700 break-words">
            {getTravelInfo(txn).from || '-'} â†’ {getTravelInfo(txn).to || '-'}
          </p>
          <p className="mt-0.5 text-slate-400">{getTravelInfo(txn).mode || 'Travel'}</p>
        </div>
      )}
      <div className="mt-2 flex items-center justify-between gap-2">
        <TransactionProofImage path={txn.attachment_path} />
        <button
          onClick={() => onView(txn)}
          className="px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 text-[11px] font-bold border border-indigo-100"
        >
          View
        </button>
      </div>
    </div>
  </motion.div>
);

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */
const MyTransactionsReport = () => {
  const { isSidebarOpen, screenWidth } = useContext(SidebarContext);
  const navigate = useNavigate();
  const role   = localStorage.getItem('role');
  const userId = localStorage.getItem('id');
  const title  = role === 'company_owner' ? 'My Transactions' : 'Transaction Report';

  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading]       = useState(false);
  const [error, setError]               = useState(null);
  const [dateRange, setDateRange]       = useState({ startDate: '', endDate: '' });
  const [filterOpen, setFilterOpen]     = useState(false);
  const [viewMode, setViewMode]         = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return 'cards';
    return 'table';
  });
  const [typeFilter, setTypeFilter]     = useState('all');
  const [projectFilter, setProjectFilter] = useState('');
  const [search, setSearch]             = useState('');
  const [isMobile, setIsMobile]         = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchTransactions = async () => {
    if (!userId || !role) { setError('Access denied'); return; }
    setIsLoading(true);
    try {
      const url = new URL(`${ApiURL}/getMyTransactionReport.php`);
      url.searchParams.append('user_id', userId);
      url.searchParams.append('role', role);
      if (dateRange.startDate) url.searchParams.append('start_date', dateRange.startDate);
      if (dateRange.endDate)   url.searchParams.append('end_date', dateRange.endDate);
      const data = await fetchJson(url.toString());
      if (data.status === 'success') { setTransactions(data.transactions || []); setError(null); }
      else setError(data.message || 'Failed to load transactions');
    } catch (e) {
      setError('Failed to load: ' + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchTransactions(); }, [userId, role, dateRange.startDate, dateRange.endDate]);

  const stats = useMemo(() => {
    const totalCredit = transactions.filter(t => t.amount_type === 'credit').reduce((s, t) => s + Number(t.credit_amount || 0), 0);
    const totalDebit  = transactions.filter(t => t.amount_type === 'debit').reduce((s, t) => s + Number(t.debit_amount || 0), 0);
    const netBalance  = transactions.length > 0 ? Number(transactions[transactions.length - 1]?.net_balance || 0) : 0;
    return { totalCredit, totalDebit, netBalance };
  }, [transactions]);

  const filtered = useMemo(() => {
    let list = [...transactions];
    if (typeFilter !== 'all') list = list.filter(t => t.amount_type === typeFilter);
    if (projectFilter) list = list.filter(t => (t.project_name || '') === projectFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        (t.particuler_to || '').toLowerCase().includes(q) ||
        (t.site || '').toLowerCase().includes(q) ||
        (t.project_name || '').toLowerCase().includes(q) ||
        (t.main_head || '').toLowerCase().includes(q) ||
        (t.sub_head || '').toLowerCase().includes(q) ||
        (t.from_destination || t.from || '').toLowerCase().includes(q) ||
        (t.to_destination || t.to || '').toLowerCase().includes(q) ||
        (t.travel_mode || '').toLowerCase().includes(q) ||
        (t.remarks || '').toLowerCase().includes(q) ||
        (t.created_by || '').toLowerCase().includes(q) ||
        (t.updated_by || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [transactions, typeFilter, projectFilter, search]);

  const exportCSV = () => {
    if (!filtered.length) return;
    const headers = [
      '#', 'Date', 'Type', 'Credit', 'Debit', 'Balance', 'Particulars', 'Site', 'Project',
      'Main Head', 'Sub Head', 'Source', 'Destination', 'Travel Mode', 'Remarks', 'Status', 'Created By', 'Created At', 
      'Updated By', 'Updated At'
    ];
    const rows = filtered.map((t, i) => [
      i+1, fmtDateTime(t.transaction_date), t.amount_type||'',
      t.credit_amount||0, t.debit_amount||0, t.net_balance||0,
      t.particuler_to||'', t.site||'', t.project_name||'', t.main_head||'', t.sub_head||'',
      t.from_destination || t.from || '', t.to_destination || t.to || '', t.travel_mode || '',
      t.remarks||'', t.form_status||'', t.created_by||'', fmtDateTime(t.created_at),
      t.updated_by||'', fmtDateTime(t.updated_at),
    ]);
    const csv = [headers,...rows].map(r => r.map(c => {
      const v = String(c ?? '');
      return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g,'""')}"` : v;
    }).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    a.download = 'transactions_report.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const projectOptions = useMemo(() => [...new Set(transactions.map(t => t.project_name).filter(Boolean))].sort(), [transactions]);
  const hasActiveFilter = dateRange.startDate || dateRange.endDate || typeFilter !== 'all' || projectFilter || search;
  const showCardsView = viewMode === 'cards';
  const showTravelColumn = filtered.some(t => isTravelTransaction(t));

  return (
    <div className="min-h-screen bg-[#f0f2f8]">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <motion.main
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
          className={`flex-1 min-h-screen transition-all duration-300 ${isSidebarOpen ? 'lg:ml-72' : ''}`}
        >
          <div className="max-w-7xl mx-auto px-3 py-3 sm:px-5 sm:py-5 lg:px-6 lg:py-6">

            {/* â”€â”€ Page header â”€â”€ */}
            <div className="flex items-center justify-between mb-3 sm:mb-5 gap-2">
              <div>
                <h1 className="text-base sm:text-lg font-bold text-slate-800 leading-tight">{title}</h1>
                <p className="text-[10px] sm:text-[11px] text-slate-400 mt-0.5">
                  {filtered.length} of {transactions.length} transactions
                </p>
              </div>

              <div className="flex items-center gap-1.5 sm:gap-2">

                {/* View toggle - visible on ALL screen sizes */}
                <div className="flex items-center bg-white border border-slate-200 rounded-lg sm:rounded-xl p-0.5 sm:p-1 shadow-sm">
                  <button
                    onClick={() => setViewMode('table')}
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-md sm:rounded-lg flex items-center justify-center transition-all touch-manipulation ${
                      viewMode === 'table' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'
                    }`}
                    aria-label="Table view"
                  >
                    <TableCellsIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('cards')}
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-md sm:rounded-lg flex items-center justify-center transition-all touch-manipulation ${
                      viewMode === 'cards' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'
                    }`}
                    aria-label="Card view"
                  >
                    <Squares2X2Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                </div>

                {/* Filter */}
                <button
                  onClick={() => setFilterOpen(p => !p)}
                  className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 h-8 sm:h-9 rounded-xl border text-xs font-semibold transition-all shadow-sm touch-manipulation ${
                    hasActiveFilter ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <FunnelIcon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Filter</span>
                  {hasActiveFilter && <span className="w-1.5 h-1.5 rounded-full bg-white/80" />}
                </button>

                {/* Export */}
                <button
                  onClick={exportCSV}
                  disabled={!filtered.length}
                  className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 h-8 sm:h-9 rounded-xl bg-white border border-slate-200 text-slate-600 text-xs font-semibold hover:border-slate-300 transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation"
                >
                  <DocumentArrowDownIcon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Export</span>
                </button>

                {/* Refresh */}
                <button
                  onClick={fetchTransactions}
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-indigo-600 transition-all shadow-sm touch-manipulation"
                >
                  <ArrowPathIcon className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                </button>

              </div>
            </div>

            {/* Filter panel */}
            <AnimatePresence>
              {filterOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }}
                  className="overflow-hidden mb-4"
                >
                  <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Filters</p>
                      {hasActiveFilter && (
                        <button onClick={() => { setDateRange({ startDate: '', endDate: '' }); setTypeFilter('all'); setProjectFilter(''); setSearch(''); }}
                          className="text-[11px] text-rose-500 font-semibold hover:text-rose-600 touch-manipulation">Clear all</button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                      <div className="sm:col-span-2 grid grid-cols-2 gap-2">
                        {[
                          { label: 'From', key: 'startDate' },
                          { label: 'To',   key: 'endDate'   },
                        ].map(({ label, key }) => (
                          <div key={key}>
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">{label}</label>
                            <div className="relative">
                              <CalendarDaysIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300 pointer-events-none" />
                              <input type="date" value={dateRange[key]}
                                onChange={e => setDateRange(p => ({ ...p, [key]: e.target.value }))}
                                className="w-full h-9 pl-8 pr-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all" />
                            </div>
                          </div>
                        ))}
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Type</label>
                        <div className="flex gap-1 h-9 bg-slate-50 border border-slate-200 rounded-xl p-1">
                          {['all', 'credit', 'debit'].map(v => (
                            <button key={v} onClick={() => setTypeFilter(v)}
                              className={`flex-1 text-[11px] font-bold rounded-lg capitalize transition-all touch-manipulation ${
                                typeFilter === v
                                  ? v === 'credit' ? 'bg-emerald-500 text-white shadow-sm'
                                  : v === 'debit'  ? 'bg-red-500 text-white shadow-sm'
                                  : 'bg-indigo-600 text-white shadow-sm'
                                  : 'text-slate-500 hover:text-slate-700'
                              }`}
                            >{v}</button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Project</label>
                        <select
                          value={projectFilter}
                          onChange={(e) => setProjectFilter(e.target.value)}
                          className="w-full h-9 px-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                        >
                          <option value="">All Projects</option>
                          {projectOptions.map(project => <option key={project} value={project}>{project}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Search</label>
                      <div className="relative">
                        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                          placeholder="Search by party, site, head, remarks, created by, updated by..."
                          className="w-full h-9 px-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all" />
                        {search && (
                          <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 touch-manipulation">
                            <XMarkIcon className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
              <StatCard label="Total Credit" value={fmt(stats.totalCredit)} icon={ArrowTrendingUpIcon}
                bg="bg-emerald-50" iconBg="bg-emerald-500" textColor="text-emerald-700" delay={0} />
              <StatCard label="Total Debit" value={fmt(stats.totalDebit)} icon={ArrowTrendingDownIcon}
                bg="bg-red-50" iconBg="bg-red-500" textColor="text-red-700" delay={0.05} />
              <StatCard label="Net Balance" value={fmt(stats.netBalance)} icon={ScaleIcon}
                bg={stats.netBalance >= 0 ? 'bg-sky-50' : 'bg-orange-50'}
                iconBg={stats.netBalance >= 0 ? 'bg-sky-500' : 'bg-orange-500'}
                textColor={stats.netBalance >= 0 ? 'text-sky-700' : 'text-orange-700'} delay={0.1} />
            </div>

            {/* Admin fund request banner */}
            {role === 'admin' && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                className="mb-4 bg-white border border-violet-200 rounded-2xl p-4 flex items-center justify-between gap-3 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
                    <BuildingOffice2Icon className="w-4.5 h-4.5 text-violet-600 w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-[13px] font-bold text-slate-800">Request Funds</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">Submit a fund request to your company owner</p>
                  </div>
                </div>
                <button onClick={() => navigate('/admin-fund-request')}
                  className="flex items-center gap-1.5 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold rounded-xl transition-colors flex-shrink-0 touch-manipulation">
                  Request <ChevronRightIcon className="w-3.5 h-3.5" />
                </button>
              </motion.div>
            )}

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl px-4 py-3">{error}</div>
            )}

            {isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-xl h-14 animate-pulse border border-slate-100" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 py-16 flex flex-col items-center gap-3 shadow-sm">
                <BanknotesIcon className="w-10 h-10 text-slate-200" />
                <p className="text-sm font-semibold text-slate-400">No transactions found</p>
                {hasActiveFilter && (
                  <button
                    onClick={() => { setDateRange({ startDate: '', endDate: '' }); setTypeFilter('all'); setSearch(''); }}
                    className="text-xs text-indigo-500 font-semibold touch-manipulation"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* CARD VIEW */}
                {showCardsView && (
                  <div className="space-y-2.5">
                    {filtered.map((txn, idx) => (
                      <TransactionCard key={`card-${txn.transaction_no || idx}`} txn={txn} index={idx} role={role} navigate={navigate} onView={setSelectedTransaction} />
                    ))}
                    <div className="flex justify-center pt-3 pb-2">
                      <button onClick={exportCSV} disabled={!filtered.length}
                        className="flex items-center gap-2 px-5 py-2.5 bg-indigo-50 text-indigo-700 text-xs font-bold rounded-xl active:bg-indigo-100 transition-colors touch-manipulation">
                        <DocumentArrowDownIcon className="w-4 h-4" />
                        Export CSV ({filtered.length} transactions)
                      </button>
                    </div>
                  </div>
                )}

                {/* TABLE VIEW */}
                {!showCardsView && (
                  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-[12px] text-left border-collapse min-w-[1400px]">
                        <thead>
                          <tr className="border-b border-slate-100 bg-slate-50/80">
                            {[
                              '#', 'Date', 'Type', 'Credit', 'Debit', 'Balance',
                              'Particulars', 'Site', 'Project', 'Head', 'Remarks', 'Proof', 'Status', 
                              'Created By', 'Created At',
                              ...(showTravelColumn ? ['Travel'] : []),
                              'Details',
                              ...(role === 'admin' ? ['Action'] : []),
                            ].map(h => (
                              <th key={h} className="px-3 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {filtered.map((txn, i) => (
                            <motion.tr key={`${txn.transaction_no || i}-${i}`}
                              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                              transition={{ delay: Math.min(i * 0.02, 0.2) }}
                              className="hover:bg-slate-50/60 transition-colors group"
                            >
                              <td className="px-3 py-2.5 text-slate-400 font-medium">{i + 1}</td>
                              <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap font-medium">{fmtDateTime(txn.transaction_date)}</td>
                              <td className="px-3 py-2.5"><TypeBadge type={txn.amount_type} /></td>
                              <td className="px-3 py-2.5 text-emerald-600 font-semibold">
                                {txn.amount_type === 'credit' ? fmt(txn.credit_amount) : '-'}
                              </td>
                              <td className="px-3 py-2.5 text-red-600 font-semibold">
                                {txn.amount_type === 'debit' ? fmt(txn.debit_amount) : '-'}
                              </td>
                              <td className="px-3 py-2.5 text-slate-700 font-bold">{fmt(txn.net_balance)}</td>
                              <td className="px-3 py-2.5 text-slate-700 max-w-[190px] whitespace-normal break-words leading-snug">{txn.particuler_to || '-'}</td>
                              <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{txn.site || '-'}</td>
                              <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{txn.project_name || '-'}</td>
                              <td className="px-3 py-2.5">
                                {txn.main_head ? (
                                  <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-semibold rounded-full border border-indigo-100 whitespace-nowrap">
                                    {txn.main_head}
                                  </span>
                                ) : '-'}
                              </td>
                              <td className="px-3 py-2.5 text-slate-500 max-w-[170px] whitespace-normal break-words leading-snug">{txn.remarks || '-'}</td>
                              <td className="px-3 py-2.5"><TransactionProofImage path={txn.attachment_path} /></td>
                              <td className="px-3 py-2.5"><StatusBadge status={txn.form_status} /></td>
                              <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{txn.created_by || '-'}</td>
                              <td className="px-3 py-2.5 text-slate-400 text-[11px] whitespace-nowrap">{fmtDateTime(txn.created_at)}</td>
                              {showTravelColumn && (
                                <td className="px-3 py-2.5 text-slate-600 min-w-[170px]">
                                  {isTravelTransaction(txn) ? (
                                    <button
                                      type="button"
                                      onClick={() => setSelectedTransaction(txn)}
                                      className="text-left hover:text-indigo-600"
                                    >
                                      <p className="font-bold leading-snug break-words">{getTravelInfo(txn).from || '-'} to {getTravelInfo(txn).to || '-'}</p>
                                      <p className="text-[10px] text-slate-400 mt-0.5">{getTravelInfo(txn).mode || 'Travel'}</p>
                                    </button>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </td>
                              )}
                              <td className="px-3 py-2.5">
                                <button
                                  type="button"
                                  onClick={() => setSelectedTransaction(txn)}
                                  className="px-3 py-1 bg-indigo-50 border border-indigo-100 text-indigo-700 text-[10px] font-bold rounded-lg hover:bg-indigo-100 transition-colors whitespace-nowrap"
                                >
                                  View
                                </button>
                              </td>
                              {/* <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{txn.updated_by || '-'}</td>
                              <td className="px-3 py-2.5 text-slate-400 text-[11px] whitespace-nowrap">
                                {txn.updated_at && txn.updated_at !== txn.created_at ? fmtDateTime(txn.updated_at) : '-'}
                              </td> */}
                              {role === 'admin' && (
                                <td className="px-3 py-2.5">
                                  {txn.amount_type === 'credit' && (
                                    <button onClick={() => navigate(`/admin-return?highlight=${txn.transaction_no}`)}
                                      className="px-3 py-1 bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-bold rounded-lg hover:bg-amber-100 transition-colors whitespace-nowrap touch-manipulation">
                                      Return
                                    </button>
                                  )}
                                 </td>
                              )}
                            </motion.tr>
                          ))}
                        </tbody>
                       </table>
                    </div>
                    <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/40 flex items-center justify-between">
                      <p className="text-[11px] text-slate-400">
                        Showing <span className="font-bold text-slate-600">{filtered.length}</span> transactions
                      </p>
                      <button onClick={exportCSV} disabled={!filtered.length}
                        className="flex items-center gap-1.5 text-[11px] font-bold text-indigo-600 hover:text-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation">
                        <DocumentArrowDownIcon className="w-3.5 h-3.5" />
                        Export CSV
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </motion.main>
      </div>
      <TransactionDetailModal txn={selectedTransaction} onClose={() => setSelectedTransaction(null)} />
    </div>
  );
};

export default MyTransactionsReport;



